import { ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { useState, useEffect, useRef } from "react";
import { useScreens } from "../../hooks/useScreens";
import Loader from "../common/Loader";

interface ScreenData {
  screenId: string;
  screenName: string;
}

interface RegionData {
  regionName: string;
  screens: ScreenData[];
}

interface LocationData {
  city: string;
  regions: RegionData[];
}

const SchedularStep2 = ({ selected, setSelected }: any) => {
  const [selectAll, setSelectAll] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedOrientation, setSelectedOrientation] = useState("landscape");
  const [selectedType, setSelectedType] = useState<
    "all" | "selected" | "unselected"
  >("all");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search]);

  const { cityWithRegions, isCityWithRegionsLoading } = useScreens(
    {},
    {
      search: debouncedSearch || undefined,
      orientation: selectedOrientation || undefined,
    }
  );

  /** Toggle individual screen selection */
  const toggleScreen = (screenId: string) => {
    setSelected((prev: string[]) =>
      prev.includes(screenId)
        ? prev.filter((id) => id !== screenId)
        : [...prev, screenId]
    );
  };

  /** Toggle all screens globally */
  const handleSelectAll = () => {
    if (selectAll) {
      setSelected([]);
    } else {
      const allScreens =
        cityWithRegions?.flatMap((city: LocationData) =>
          city.regions.flatMap((region: RegionData) =>
            region.screens.map((screen: ScreenData) => screen.screenId)
          )
        ) || [];
      setSelected(allScreens);
    }
    setSelectAll(!selectAll);
  };

  /** Toggle all screens in a city */
  const toggleCity = (city: LocationData) => {
    const cityScreens = city.regions.flatMap((region) =>
      region.screens.map((screen) => screen.screenId)
    );
    const allSelected = cityScreens.every((id) => selected.includes(id));

    setSelected((prev: string[]) =>
      allSelected
        ? prev.filter((id) => !cityScreens.includes(id))
        : [...prev, ...cityScreens.filter((id) => !prev.includes(id))]
    );
  };

  /** Toggle all screens in a region */
  const toggleRegion = (region: RegionData) => {
    const regionScreens = region.screens.map((screen) => screen.screenId);
    const allSelected = regionScreens.every((id) => selected.includes(id));

    setSelected((prev: string[]) =>
      allSelected
        ? prev.filter((id) => !regionScreens.includes(id))
        : [...prev, ...regionScreens.filter((id) => !prev.includes(id))]
    );
  };

  /** Auto-update global "Select All" checkbox */
  useEffect(() => {
    const totalScreens =
      cityWithRegions?.reduce(
        (acc, city) =>
          acc +
          city.regions.reduce(
            (rAcc, region) => rAcc + region.screens.length,
            0
          ),
        0
      ) || 0;

    setSelectAll(totalScreens > 0 && selected.length === totalScreens);
  }, [selected, cityWithRegions]);

  /** Filter cities based on selectedType (search and orientation are handled by backend) */
  const filteredCities = cityWithRegions
    ?.map((city) => ({
      ...city,
      regions: city.regions
        .map((region) => ({
          ...region,
          screens: region.screens.filter((screen) => {
            const isSelected = selected.includes(screen.screenId);

            if (selectedType === "all") return true;
            if (selectedType === "selected") return isSelected;
            if (selectedType === "unselected") return !isSelected;
            return true;
          }),
        }))
        .filter((region) => region.screens.length > 0),
    }))
    .filter((city) => city.regions.length > 0);

  // if (isCityWithRegionsLoading) {
  //   return (
  //     <div className="flex justify-center items-center h-64">
  //       <Loader />
  //     </div>
  //   );
  // }

  return (
    <div>
      {/* Top Bar with Orientation and Filter Buttons */}
      <div className="flex flex-1 justify-center gap-3 mb-4">
        {/* Orientation Buttons */}
        <button
          className={`px-5 py-1 rounded border font-medium flex items-center transition-all duration-150 ${
            selectedOrientation === "landscape"
              ? "bg-primary-default text-white"
              : "bg-white text-primary-default border-primary-default hover:bg-primary-light hover:text-white"
          }`}
          onClick={() => setSelectedOrientation("landscape")}
        >
          Landscape
        </button>
        <button
          className={`px-5 py-1 rounded border font-medium flex items-center transition-all duration-150 ${
            selectedOrientation === "portrait"
              ? "bg-primary-default text-white"
              : "bg-white text-primary-default border-primary-default hover:bg-primary-light hover:text-white"
          }`}
          onClick={() => setSelectedOrientation("portrait")}
        >
          Portrait
        </button>

        {/* Search Box */}
        <input
          type="text"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none"
        />

        {/* View Filter Buttons */}
        <button
          className={`px-5 py-1 rounded border font-medium flex items-center transition-all duration-150 ${
            selectedType === "all"
              ? "bg-secondary-default text-white"
              : "bg-white text-secondary-default border-secondary-default hover:bg-secondary-light hover:text-white"
          }`}
          onClick={() => setSelectedType("all")}
        >
          All
        </button>
        <button
          className={`px-5 py-1 rounded border font-medium flex items-center transition-all duration-150 ${
            selectedType === "selected"
              ? "bg-secondary-default text-white"
              : "bg-white text-secondary-default border-secondary-default hover:bg-secondary-light hover:text-white"
          }`}
          onClick={() => setSelectedType("selected")}
        >
          Selected
        </button>
        <button
          className={`px-5 py-1 rounded border font-medium flex items-center transition-all duration-150 ${
            selectedType === "unselected"
              ? "bg-secondary-default text-white"
              : "bg-white text-secondary-default border-secondary-default hover:bg-secondary-light hover:text-white"
          }`}
          onClick={() => setSelectedType("unselected")}
        >
          Unselected
        </button>
      </div>

      {/* Global Select All */}
      <div className="mb-2">
        <label>
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
          />
          &nbsp; Select All
        </label>
      </div>

      {isCityWithRegionsLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      ) : (
        filteredCities?.map((city) => (
          <div key={city.city} className="mb-4 border p-3 rounded">
            {/* City Checkbox */}
            <div className="font-bold mb-2">
              <label>
                <input
                  type="checkbox"
                  checked={city.regions
                    .flatMap((region) => region.screens)
                    .every((screen) => selected.includes(screen.screenId))}
                  onChange={() => toggleCity(city)}
                />
                &nbsp; {city.city}
              </label>
            </div>

            {/* Regions */}
            {city.regions.map((region) => (
              <div key={region.regionName} className="mb-3 pl-5">
                <div className="font-semibold mb-2">
                  <label>
                    <input
                      type="checkbox"
                      checked={region.screens.every((screen) =>
                        selected.includes(screen.screenId)
                      )}
                      onChange={() => toggleRegion(region)}
                    />
                    &nbsp; {region.regionName}
                  </label>
                </div>

                {/* Screens */}
                <div className="grid grid-cols-3 gap-3 pl-5">
                  {region.screens.map((screen) => (
                    <div
                      key={screen.screenId}
                      onClick={() => toggleScreen(screen.screenId)}
                      className={`cursor-pointer py-3 px-2 bg-white rounded border flex flex-col items-center ${
                        selected.includes(screen.screenId)
                          ? "border-primary-default"
                          : "border-gray-300"
                      }`}
                    >
                      <ComputerDesktopIcon className="h-7 w-7" />
                      <span className="mt-1 text-sm">{screen.screenName}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {/* City and Region Listing */}
      {}
    </div>
  );
};

export default SchedularStep2;
